import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";

const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4">
      <div className="relative z-10 w-full max-w-md" data-testid="forgot-root">
        <div className="flex items-center justify-between gap-4">
          <Logo size="md" testId="forgot-logo" />
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
        <h1 className="mt-8 text-3xl font-extrabold">Reset your password</h1>
        <p className="mt-2 text-sm text-slate-500">
          Enter the email you signed up with and we'll help you get back in.
        </p>

        {result ? (
          <div className="mt-8 card-flat p-6" data-testid="forgot-result">
            <p className="text-sm text-slate-600">{result.message}</p>
            {result.email_disabled && (
              <p className="mt-4 text-xs text-slate-500">
                Email delivery isn't switched on yet, so we can't send you a link. If you're locked out, contact us and
                we'll reset it by hand. If you can still sign in, change your password from{" "}
                <span className="font-semibold text-slate-600">Profile → Security</span>.
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 card-flat p-6 space-y-4">
            <div>
              <label className="rule-label">Email</label>
              <input data-testid="forgot-email" type="email" className={`${field} mt-2`} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <button data-testid="forgot-submit" disabled={busy} className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              <span className="inline-flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> {busy ? "Working…" : "Send reset link"}
              </span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
